import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from 'react-redux';
import Modal from 'react-bootstrap/Modal';

import {
    fetchLessonsMark
} from '../../../Admin/Lessons/actions';
import { fetchIndices } from '../../../Admin/Indices/actions';
import { fetchClassRooms } from '../../../School/ClassRooms/actions';
import Footer from "../../../Footer/FooterBottom";
import ClassRoomWithCertificate from "../../../Admin/Question/View/Background/ClassRoomWithCertificate";
import { setIndeices } from "../../../../context/sessionStorage";

import api from '../../../../api/api';
import LessonResult from '../../Tracks/LessonView/LessonResult';
import ShowQuestionModal from '../../Tracks/ExamView/ShowQuestionModal';
import QuestionView from '../../../Admin/Question/View/QuestionView';
import { adaptMissionData } from '../../../School/Missions/missionAnswerAdapter';

import { useUserMarksSearch } from './hooks/useUserMarksSearch';
import UserMarksHeader from './components/UserMarksHeader';
import UserMarksStatsOverview from './components/UserMarksStatsOverview';
import UserMarksFilterBar from './components/UserMarksFilterBar';
import UserMarksList from './components/UserMarksList';

import "../../Tracks/LessonView/LessonResult/summary.css";

const UserMarks = () => {
    const { t } = useTranslation('common');
    const dispatch = useDispatch();

    // Redux State
    const user = useSelector(state => state.user);
    const indicesState = useSelector(state => state.indices);
    const lessonsState = useSelector(state => state.lessons);

    const lessons = lessonsState?.lessons || [];
    const isLoaded = lessonsState?.isLoaded || false;

    const [currentStatus, setCurrentStatus] = useState("");

    // LessonResult Modal State
    const [activeSummary, setActiveSummary] = useState(null); // { lesson, attempt }
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryLessonData, setSummaryLessonData] = useState(null);
    const [activeQuestion, setActiveQuestion] = useState(null);
    const [showQuestionModal, setShowQuestionModal] = useState(false);

    // Custom Hook for Search, Filtering & Statistics
    const {
        searchTerm,
        setSearchTerm,
        searchMode,
        groupLesson,
        setGroupLesson,
        startDate,
        endDate,
        dateString,
        handleApplyDate,
        handleSetAllTime,
        selectedClassTypeId,
        setSelectedClassTypeId,
        classTypeOptions,
        scoreFilter,
        setScoreFilter,
        sortBy,
        setSortBy,
        quickTab,
        setQuickTab,
        showAdvancedSearch,
        setShowAdvancedSearch,
        stats,
        filteredLessons,
        hasActiveFilters,
        handleResetFilters
    } = useUserMarksSearch(lessons, indicesState);

    // Sync Data with Backend API
    const handleSync = useCallback((overrideGroup = groupLesson, overrideMode = searchMode) => {
        dispatch(fetchLessonsMark(startDate, endDate, null, overrideGroup, overrideMode));
    }, [dispatch, startDate, endDate, groupLesson, searchMode]);

    // Initial Mounting
    useEffect(() => {
        dispatch(fetchIndices());
        dispatch(fetchClassRooms());
        handleSync();

        if (typeof window !== 'undefined' && window.scrollToPosition) {
            window.scrollToPosition("main-user-mark-section");
        }
    }, [dispatch, handleSync]);

    // Update Session storage for indices
    useEffect(() => {
        if (indicesState) {
            setIndeices(indicesState);
        }
    }, [indicesState]);

    // Toggle groupLesson handler
    const handleToggleGroupLesson = useCallback(() => {
        const nextVal = !groupLesson;
        setGroupLesson(nextVal);
        handleSync(nextVal, searchMode);
    }, [groupLesson, searchMode, handleSync, setGroupLesson]);

    // Summary Handlers
    const handleOpenSummary = useCallback((lesson, attempt = null) => {
        const att = attempt || lesson.Attempts?.[0] || lesson;
        const attemptId = att?.Id || att?.AttemptId;
        setActiveSummary({ lesson, attempt: att });
        setSummaryLoading(true);

        const request = attemptId
            ? api.get(`/UserLessonMark/${attemptId}`)
            : api.get(`/Lessons/${lesson.Id}?ByView=false`);

        request
            .then(res => {
                const responseData = res?.data?.data || res?.data;
                if (responseData && (responseData.Questions || responseData.LessonQuestions || responseData.Id || responseData.Name)) {
                    setSummaryLessonData(responseData);
                } else {
                    setSummaryLessonData(lesson);
                }
                setSummaryLoading(false);
            })
            .catch(() => {
                setSummaryLessonData(lesson);
                setSummaryLoading(false);
            });
    }, []);

    const handleCloseSummary = useCallback(() => {
        setActiveSummary(null);
        setSummaryLessonData(null);
        setActiveQuestion(null);
        setShowQuestionModal(false);
    }, []);

    const handleOpenQuestion = useCallback((questionOrId) => {
        const qId = typeof questionOrId === 'object' && questionOrId !== null ? questionOrId.Id : questionOrId;
        const questionsList = summaryLessonData?.Questions || summaryLessonData?.LessonQuestions?.map(lq => lq.Question) || [];
        const found = questionsList.find(q => Number(q.Id) === Number(qId));
        if (found) {
            setActiveQuestion(found);
            setShowQuestionModal(true);
        }
    }, [summaryLessonData]);

    const renderQuestionView = () => {
        if (!activeQuestion) return null;
        const typeId = Number(activeQuestion.Type?.Id ?? activeQuestion.Type ?? activeQuestion.TypeId ?? 0);
        const isGame = (typeId >= 15 && typeId <= 31) || Boolean(activeQuestion.isGame);

        return (
            <QuestionView
                question={activeQuestion}
                answers={activeQuestion.Answers || activeQuestion.QuestionAnswers}
                key={activeQuestion.Id}
                index={1}
                ViewGameResult={isGame}
                showResult={!isGame}
                onEdit={false}
                solveButton={false}
            />
        );
    };

    const FinishMove = () => {
        setCurrentStatus("");
    };

    return (
        <>
            <Helmet>
                <title>
                    {t('Pages.Profile.markList') || 'سِجلّ الدرجات والعلامات'} | {t('Pages.Profile.title') || 'الملف الشخصي'}
                </title>
            </Helmet>

            <section
                className="position-relative"
                id="main-user-mark-section"
                style={{
                    minHeight: '100vh',
                    borderBottom: '1px solid #0e2c53',
                    paddingBottom: '60px'
                }}
            >
                {/* Background Animation (Kept exact background as requested) */}
                <ClassRoomWithCertificate
                    currentStatus={currentStatus}
                    FinishMove={FinishMove}
                />

                {/* Main Full-Width Container matching Teacher Missions Style */}
                <div className="container-fluid px-3 px-md-5 pt-4" data-aos="fade-top" data-aos-easing="linear">
                    {/* 1. Header */}
                    <UserMarksHeader
                        onRefresh={() => handleSync()}
                        isLoading={!isLoaded}
                        totalLessons={filteredLessons.length}
                        t={t}
                    />

                    {/* 2. Stats KPIs Overview */}
                    <UserMarksStatsOverview
                        stats={stats}
                        quickTab={quickTab}
                        onSelectQuickTab={setQuickTab}
                        t={t}
                    />

                    {/* 3. Modern Filter & Search Bar */}
                    <UserMarksFilterBar
                        dateString={dateString}
                        onApplyDate={handleApplyDate}
                        onSetAllTime={() => {
                            handleSetAllTime();
                            setTimeout(() => {
                                handleSync(groupLesson, 'all');
                            }, 50);
                        }}
                        searchMode={searchMode}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        onSync={() => handleSync()}
                        isLoading={!isLoaded}
                        groupLesson={groupLesson}
                        onToggleGroupLesson={handleToggleGroupLesson}
                        selectedClassTypeId={selectedClassTypeId}
                        onClassTypeChange={setSelectedClassTypeId}
                        classTypeOptions={classTypeOptions}
                        scoreFilter={scoreFilter}
                        onScoreFilterChange={setScoreFilter}
                        sortBy={sortBy}
                        onSortChange={setSortBy}
                        quickTab={quickTab}
                        onQuickTabChange={setQuickTab}
                        showAdvancedSearch={showAdvancedSearch}
                        onToggleAdvancedSearch={() => setShowAdvancedSearch(prev => !prev)}
                        hasActiveFilters={hasActiveFilters}
                        onResetFilters={() => {
                            handleResetFilters();
                            setTimeout(() => {
                                handleSync(true, 'date');
                            }, 50);
                        }}
                        t={t}
                    />

                    {/* 4. Lessons Marks & Attempts List */}
                    <UserMarksList
                        lessons={filteredLessons}
                        isLoaded={isLoaded}
                        lazyLoading={false}
                        user={user}
                        indices={indicesState}
                        groupLesson={groupLesson}
                        searchTerm={searchTerm}
                        onOpenSummary={handleOpenSummary}
                        t={t}
                    />
                </div>
            </section>

            {/* Question Inspection Modal */}
            {showQuestionModal && (
                <ShowQuestionModal
                    renderQuestionView={renderQuestionView}
                    showResult={true}
                    handleClose={() => {
                        setShowQuestionModal(false);
                        setActiveQuestion(null);
                    }}
                />
            )}

            {/* Direct Fullscreen LessonResult Modal */}
            {activeSummary && (
                <Modal
                    centered
                    dialogClassName="modal-mission-result-fullscreen"
                    show={Boolean(activeSummary)}
                    onHide={handleCloseSummary}
                    backdrop="static"
                    keyboard={false}
                    enforceFocus={false}
                >
                    <Modal.Body className="p-0" style={{ height: '100vh', maxHeight: '100vh', overflowY: 'auto', background: '#f8fafc' }}>
                        {summaryLoading ? (
                            <div className="d-flex flex-column align-items-center justify-content-center h-100 py-5">
                                <div className="display-4 text-primary mb-3">
                                    <i className="fad fa-sync-alt fa-spin"></i>
                                </div>
                                <h5 className="text-muted font-weight-bold">
                                    {t("Pages.Lessons.Loading") || 'جاري تحميل تفاصيل نتائج الدرس والمهارات...'}
                                </h5>
                            </div>
                        ) : (() => {
                            const lessonObj = summaryLessonData || activeSummary.lesson;
                            const questions = lessonObj?.Questions || lessonObj?.LessonQuestions?.map(lq => lq.Question || lq).filter(Boolean) || [];
                            const solves = lessonObj?.Solves || lessonObj?.LessonSolves || [];
                            const skills = lessonObj?.Skills || activeSummary.lesson?.Skills || [];

                            let exam;
                            let elapsedSeconds = 0;

                            if (solves.length > 0) {
                                const adapted = adaptMissionData(questions, solves);
                                exam = adapted.exam;
                                elapsedSeconds = adapted.elapsedSeconds || 0;
                            } else {
                                const maxMark = Number(activeSummary.attempt?.TotalMark || lessonObj?.TotalMark || 100);
                                const achievedMark = Number(activeSummary.attempt?.Mark !== undefined && activeSummary.attempt?.Mark !== null ? activeSummary.attempt.Mark : (lessonObj?.Mark || 0));
                                const scoreRatio = maxMark > 0 ? (achievedMark / maxMark) : 1;

                                exam = {
                                    current: questions.map(q => ({
                                        Id: q.Id,
                                        mark: Math.round(Number(q.Mark || 1) * scoreRatio * 10) / 10,
                                        answers: []
                                    })),
                                    marks: questions.map(q => ({
                                        Id: q.Id,
                                        userMark: Math.round(Number(q.Mark || 1) * scoreRatio * 10) / 10
                                    }))
                                };
                            }

                            if (!elapsedSeconds) {
                                const clock = activeSummary.attempt?.UserTime?.Clock || lessonObj?.UserTime?.Clock;
                                if (clock) {
                                    const h = Number(clock.hour || 0);
                                    const m = Number(clock.minutes || 0);
                                    const s = Number(clock.seconds || 0);
                                    elapsedSeconds = (h * 3600) + (m * 60) + s;
                                } else if (activeSummary.attempt?.UserTime?.duration) {
                                    elapsedSeconds = Number(activeSummary.attempt.UserTime.duration || 0);
                                }
                            }

                            return (
                                <LessonResult
                                    questions={questions}
                                    exam={exam}
                                    elapsedSeconds={elapsedSeconds}
                                    lesson={{ ...lessonObj, Skills: skills }}
                                    lessonId={lessonObj?.Id}
                                    lessonTitle={lessonObj?.Name || lessonObj?.title || ''}
                                    user={user}
                                    isMission={true}
                                    hideLevel={false}
                                    hideReset={true}
                                    onOpenQuestion={handleOpenQuestion}
                                    onClose={handleCloseSummary}
                                />
                            );
                        })()}
                    </Modal.Body>
                </Modal>
            )}

            <div className="position-sticky">
                <Footer />
            </div>
        </>
    );
};

export default React.memo(UserMarks);
